import { useState, useEffect, useRef, useCallback, FormEvent, ChangeEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@/lib/i18n/client';
import { useCreateReview } from '@/lib/hooks/use-reviews';
import { authService, UserProfile } from '@/lib/services/auth.service';
import { InteractiveStarRating } from './InteractiveStarRating';
import {
  Overlay,
  ModalPanel,
  ModalHeader,
  ModalTitle,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  Form,
  FieldGroup,
  Label,
  Input,
  TextArea,
  ErrorText,
  Row,
  PhotoSection,
  PhotoHint,
  PhotoPreviewRow,
  PhotoPreviewContainer,
  PhotoPreviewImg,
  RemovePhotoButton,
  HiddenFileInput,
  AddPhotoButton,
  StatusContainer,
  StatusIcon,
  StatusTitle,
  StatusMessage,
  RetryButton,
  SubmitButton,
} from './WriteReviewModal.styled';

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

interface FormErrors {
  rating?: string;
  title?: string;
  content?: string;
}

interface WriteReviewModalProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly productId: string;
}

const MAX_PHOTOS = 3;

export function WriteReviewModal({ isOpen, onClose, productId }: WriteReviewModalProps) {
  const { t } = useTranslation('pdp');
  const createReview = useCreateReview();

  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [photos, setPhotos] = useState<File[]>([]);
  const [errors, setErrors] = useState<FormErrors>({});
  const [status, setStatus] = useState<FormStatus>('idle');
  const [customerId, setCustomerId] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const firstFieldRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    if (authService.isAuthenticated()) {
      authService.fetchCurrentUser().then((user: UserProfile) => {
        if (cancelled) return;
        setCustomerId(user.id);
        const fullName = [user.profile.firstName, user.profile.lastName]
          .filter(Boolean)
          .join(' ');
        if (fullName) setName(fullName);
        if (user.email) setEmail(user.email);
      }).catch(() => {
        if (!cancelled) setCustomerId(crypto.randomUUID());
      });
    } else {
      setCustomerId(crypto.randomUUID());
    }

    return () => { cancelled = true; };
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && status === 'idle') {
      const timer = setTimeout(() => firstFieldRef.current?.focus(), 200);
      return () => clearTimeout(timer);
    }
  }, [isOpen, status]);

  // Lock body scroll while open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  // ESC to close
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const resetForm = useCallback(() => {
    setRating(0);
    setTitle('');
    setContent('');
    setName('');
    setEmail('');
    setPhotos([]);
    setErrors({});
    setStatus('idle');
    setCustomerId('');
  }, []);

  const handleClose = useCallback(() => {
    resetForm();
    onClose();
  }, [resetForm, onClose]);

  useEffect(() => {
    if (status !== 'success') return;
    const timer = setTimeout(handleClose, 3000);
    return () => clearTimeout(timer);
  }, [status, handleClose]);

  const validate = (): FormErrors => {
    const newErrors: FormErrors = {};
    if (!rating) newErrors.rating = t('reviews.form.ratingRequired');
    if (!title.trim()) newErrors.title = t('reviews.form.titleRequired');
    if (!content.trim()) newErrors.content = t('reviews.form.bodyRequired');
    return newErrors;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setErrors({});
    setStatus('submitting');

    try {
      await createReview.mutateAsync({
        productId,
        customerId,
        rating,
        title: title.trim(),
        content: content.trim(),
      });
      setStatus('success');
    } catch {
      setStatus('error');
    }
  };

  const handlePhotoAdd = (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const newPhotos = [...photos, ...Array.from(files)].slice(0, MAX_PHOTOS);
    setPhotos(newPhotos);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handlePhotoRemove = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index));
  };

  const isDisabled = status === 'submitting';

  if (!isOpen) return null;

  const renderSuccessBody = () => (
    <ModalBody>
      <StatusContainer>
        <StatusIcon variant="success">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z" />
          </svg>
        </StatusIcon>
        <StatusTitle>{t('reviews.form.successTitle')}</StatusTitle>
        <StatusMessage>{t('reviews.form.successMessage')}</StatusMessage>
      </StatusContainer>
    </ModalBody>
  );

  const renderErrorBody = () => (
    <ModalBody>
      <StatusContainer>
        <StatusIcon variant="error">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        </StatusIcon>
        <StatusTitle>{t('reviews.form.errorTitle')}</StatusTitle>
        <StatusMessage>{t('reviews.form.errorMessage')}</StatusMessage>
        <RetryButton type="button" onClick={() => setStatus('idle')}>
          {t('reviews.form.retry')}
        </RetryButton>
      </StatusContainer>
    </ModalBody>
  );

  const renderFormBody = () => (
    <>
      <ModalBody>
        <Form id="write-review-form" onSubmit={handleSubmit}>
          <FieldGroup ref={firstFieldRef} tabIndex={-1}>
            <Label>{t('reviews.form.ratingLabel')}</Label>
            <InteractiveStarRating
              value={rating}
              onChange={setRating}
              size="lg"
              disabled={isDisabled}
            />
            {errors.rating && <ErrorText>{errors.rating}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="review-title">{t('reviews.form.titleLabel')}</Label>
            <Input
              id="review-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('reviews.form.titlePlaceholder')}
              maxLength={200}
              disabled={isDisabled}
              hasError={!!errors.title}
            />
            {errors.title && <ErrorText>{errors.title}</ErrorText>}
          </FieldGroup>

          <FieldGroup>
            <Label htmlFor="review-content">{t('reviews.form.bodyLabel')}</Label>
            <TextArea
              id="review-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder={t('reviews.form.bodyPlaceholder')}
              maxLength={5000}
              disabled={isDisabled}
              hasError={!!errors.content}
            />
            {errors.content && <ErrorText>{errors.content}</ErrorText>}
          </FieldGroup>

          <Row>
            <FieldGroup>
              <Label htmlFor="review-name">{t('reviews.form.nameLabel')}</Label>
              <Input
                id="review-name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('reviews.form.namePlaceholder')}
                disabled={isDisabled}
              />
            </FieldGroup>

            <FieldGroup>
              <Label htmlFor="review-email">{t('reviews.form.emailLabel')}</Label>
              <Input
                id="review-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('reviews.form.emailPlaceholder')}
                disabled={isDisabled}
              />
            </FieldGroup>
          </Row>

          {/* Photo upload UI-only: backend does not yet accept image_url */}
          <PhotoSection>
            <Label>{t('reviews.form.photoLabel')}</Label>
            <PhotoHint>{t('reviews.form.photoHint')}</PhotoHint>
            <PhotoPreviewRow>
              {photos.map((file, idx) => (
                <PhotoPreviewContainer key={`${file.name}-${idx}`}>
                  <PhotoPreviewImg
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                  />
                  <RemovePhotoButton
                    type="button"
                    onClick={() => handlePhotoRemove(idx)}
                    aria-label={`Remove photo ${idx + 1}`}
                    disabled={isDisabled}
                  >
                    &times;
                  </RemovePhotoButton>
                </PhotoPreviewContainer>
              ))}
              {photos.length < MAX_PHOTOS && (
                <AddPhotoButton
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isDisabled}
                  aria-label="Add photo"
                >
                  +
                </AddPhotoButton>
              )}
            </PhotoPreviewRow>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png"
              multiple
              onChange={handlePhotoAdd}
            />
          </PhotoSection>
        </Form>
      </ModalBody>

      <ModalFooter>
        <SubmitButton
          type="submit"
          form="write-review-form"
          disabled={isDisabled}
        >
          {isDisabled
            ? t('reviews.form.submitting')
            : t('reviews.form.submit')}
        </SubmitButton>
      </ModalFooter>
    </>
  );

  const renderContent = () => {
    if (status === 'success') return renderSuccessBody();
    if (status === 'error') return renderErrorBody();
    return renderFormBody();
  };

  return createPortal(
    <>
      <Overlay onClick={handleClose} data-testid="modal-overlay" />
      <ModalPanel
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={t('reviews.form.modalTitle')}
        data-testid="modal-panel"
      >
        <ModalHeader>
          <ModalTitle>{t('reviews.form.modalTitle')}</ModalTitle>
          <ModalCloseButton onClick={handleClose} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </ModalCloseButton>
        </ModalHeader>

        {renderContent()}
      </ModalPanel>
    </>,
    document.body,
  );
}
